import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { CONTACT, FREE_EDIT_LABEL } from "../../data/site";
import { HeroCards, HeroCardsMobile } from "./HeroCards";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.15 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const ghostY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  const scrollToWork = () => window.__lenis?.scrollTo("#work", { offset: -70 });

  return (
    <section ref={ref} data-testid="hero" className="relative bg-black overflow-hidden">
      <motion.p
        style={{ y: ghostY }}
        aria-hidden="true"
        className="pointer-events-none select-none absolute -top-6 left-1/2 -translate-x-1/2 font-heading font-black text-[26vw] leading-none text-transparent whitespace-nowrap"
      >
        <span style={{ WebkitTextStroke: "1px rgba(255,255,255,0.05)" }}>10,000</span>
      </motion.p>

      <HeroCards progress={scrollYProgress} />

      <div className="container-x relative pt-40 pb-24 md:pt-52 md:pb-36 text-center">
        <h1 className="font-heading font-black tracking-tighter leading-[1.0] text-white text-[11vw] sm:text-7xl lg:text-[5.4rem]">
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">
              Your editing,
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block">
              <span className="text-[#C8F031]">handled.</span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          data-testid="hero-subline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-7 mx-auto max-w-[520px] text-sm sm:text-base text-white/50 leading-relaxed"
        >
          10,000+ videos edited for 200+ creators, brands and agencies.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
          className="mt-9 flex flex-col items-center gap-4"
        >
          <a
            data-testid="hero-cta-free-edit"
            href={CONTACT.freeEditLink}
            target="_blank"
            rel="noreferrer"
            className="btn-primary"
          >
            {FREE_EDIT_LABEL} <ArrowRight className="w-4 h-4" />
          </a>
          <button
            data-testid="hero-cta-work"
            onClick={scrollToWork}
            className="text-sm text-white/50 hover:text-[#C8F031] transition-colors min-h-[44px] font-heading font-semibold"
          >
            or see the work →
          </button>
        </motion.div>
        <HeroCardsMobile />
      </div>
    </section>
  );
};

export default Hero;
